import React from 'react'
import { Row, Col, FormGroup, Button, Spinner } from 'reactstrap'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  handleSubmitCreateEmailReportEntry,
  handleCompanySelectedEmailReport,
  handleSubmitSaveEmailReportEntry,
} from '../actions/emailReportsActions'
import PageContainer from '../Shared/LayoutComponents/PageContainer'
import HeaderRow from '../Shared/LayoutComponents/HeaderRow'
import PageHeading from '../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../Shared/LoadingSpinner'
import useFetchingReportCompany from './useFetchingReportCompany'
import SelectField from '../CompanyDetails/Settings/SelectField'
import Checkbox from '../CompanyDetails/Settings/Checkbox'
import CreatableSelectField from './CreatableSelectField'
import DateSelectField from './DateSelectField'

const reportSchema = Yup.object().shape({
  includeAdditionExclusions: Yup.bool(),
  includeInactiveUsers: Yup.bool(),
  includeIgnoredUsers: Yup.bool(),
  includeVeeamReports: Yup.bool(),
  selectedDayOfMonth: Yup.object()
    .typeError('Select a day of the month to run the report.')
    .shape({
      label: Yup.number(),
      value: Yup.number(),
    })
    .required('A day of the month is required'),
  startDate: Yup.date().typeError('A start date is required').required('A start date is required'),
  selectedCompany: Yup.object()
    .typeError('A company is required')
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .required('A company is required'),
  emailAddresses: Yup.array()
    .typeError('Atleast 1 email address is required')
    .of(
      Yup.object()
        .shape({
          label: Yup.string().email('Invalid Email address'),
          value: Yup.string().email('Invalid Email address'),
        })
        .required('An email address is required')
    )
    .required('Atleast 1 email address is required'),
})

const CreateEmailReport = (props) => {
  useFetchingReportCompany(props.location && props.location.state ? props.location.state.existingId : null)

  if (props.companiesLoading) {
    return <LoadingSpinner />
  } else {
    let initialValues
    if (props.loadedReport !== null) {
      initialValues = {
        id: props.loadedReport.id,
        companies: props.allCompanyList,
        dayOfMonthOptions: props.dayOfMonthOptions,
        selectedCompany: props.loadedReport.company,
        includeIgnored: props.loadedReport.includeIgnoredUsers,
        includeVeeamReports: props.loadedReport.includeVeeamReports,
        includeInactive: props.loadedReport.includeInactiveUsers,
        includeExcluded: props.loadedReport.includeAdditionExclusions,
        emailAddresses: props.loadedReport.recipients,
        startDate: props.loadedReport.start,
        selectedDayOfMonth: { label: props.loadedReport.dayOfMonth, value: props.loadedReport.dayOfMonth },
        isEdit: true,
      }
    } else {
      initialValues = {
        companies: props.allCompanyList,
        dayOfMonthOptions: props.dayOfMonthOptions,
        selectedCompany: null,
        includeIgnored: false,
        includeInactive: false,
        includeExcluded: false,
        includeVeeamReports: false,
        emailAddresses: null,
        startDate: null,
        selectedDayOfMonth: null,
        isEdit: false,
      }
    }

    return (
      <PageContainer>
        <HeaderRow center>
          <PageHeading>
            {props.loadedReport !== null ? 'Edit Email Report Schedule' : 'Create a New Email Report Schedule'}
          </PageHeading>
        </HeaderRow>
        <Row>
          <Col sm={0} md={2}></Col>
          <Col sm={12} md={8}>
            <Formik
              initialValues={initialValues}
              onSubmit={async (values, { setSubmitting, setStatus }) => {
                try {
                  if (values && values.isEdit) {
                    await props.handleSubmitSaveEmailReportEntry(values)
                  } else {
                    await props.handleSubmitCreateEmailReportEntry(values)
                  }
                  setSubmitting(false)
                } catch (err) {
                  setSubmitting(false)
                  setStatus({ msg: err })
                }
              }}
              validateOnChange={true}
              validationSchema={reportSchema}
            >
              {(props) => {
                const { touched, errors, values, isSubmitting, handleSubmit, setFieldValue, setFieldTouched } = props

                return (
                  <form onSubmit={handleSubmit}>
                    <FormGroup row>
                      <Col sm={8}>
                        <SelectField
                          id="selectedCompany"
                          name="selectedCompany"
                          label="Select a Company"
                          placeholder="Select a Company"
                          options={values ? values.companies : null}
                          value={values ? values.selectedCompany : null}
                          isMulti={false}
                          isDisabled={values && values.isEdit}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          touched={touched.selectedCompany}
                          errorObject={errors.selectedCompany}
                          isClearable={true}
                          backspaceRemovesValue={true}
                          autoComplete="off"
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={8}>
                        <CreatableSelectField
                          id="emailAddresses"
                          name="emailAddresses"
                          label="Report Email Address Recipients"
                          placeholder="Type email addresses"
                          options={values ? [] : null}
                          value={values ? values.emailAddresses : null}
                          isMulti={true}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          touched={touched.emailAddresses}
                          errorList={errors.emailAddresses}
                          isClearable={true}
                          backspaceRemovesValue={true}
                          autoComplete="off"
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={4}>
                        <SelectField
                          id="selectedDayOfMonth"
                          name="selectedDayOfMonth"
                          label="Day of the month to generate report"
                          placeholder="Select a Day"
                          options={values ? values.dayOfMonthOptions : null}
                          value={values ? values.selectedDayOfMonth : null}
                          isMulti={false}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          touched={touched.selectedDayOfMonth}
                          error={errors.selectedDayOfMonth}
                          isClearable={true}
                          backspaceRemovesValue={true}
                          autoComplete="off"
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={12}>
                        <DateSelectField
                          label="Start Date"
                          name="startDate"
                          value={values.startDate}
                          touched={touched.startDate}
                          error={errors.startDate}
                          onChange={setFieldValue}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={12}>
                        <Field
                          name="includeIgnored"
                          type="checkbox"
                          fieldName="Include Ignored users in report"
                          checked={values ? values.includeIgnored : false}
                          component={Checkbox}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={12}>
                        <Field
                          name="includeVeeamReports"
                          type="checkbox"
                          fieldName="Include Veeam reports for client (if applicable)"
                          checked={values ? values.includeVeeamReports : false}
                          component={Checkbox}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={12}>
                        <Field
                          name="includeInactive"
                          type="checkbox"
                          fieldName="Include Inactive users in report"
                          checked={values ? values.includeInactive : false}
                          component={Checkbox}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={12}>
                        <Field
                          name="includeExcluded"
                          type="checkbox"
                          fieldName="Include Excluded user information in report"
                          checked={values ? values.includeExcluded : false}
                          component={Checkbox}
                        />
                      </Col>
                    </FormGroup>
                    <div>
                      <Link to="/EmailReport">
                        <Button outline color="secondary">
                          Back
                        </Button>
                      </Link>{' '}
                      {values && values.isEdit ? (
                        <Button className="float-right" type="submit" disabled={isSubmitting} color="primary">
                          {isSubmitting ? (
                            <span>
                              <Spinner style={{ height: '1.15rem', width: '1.15rem' }} color="light" /> Saving
                            </span>
                          ) : (
                            'Save'
                          )}
                        </Button>
                      ) : (
                        <Button className="float-right" type="submit" disabled={isSubmitting} color="primary">
                          {isSubmitting ? (
                            <span>
                              <Spinner style={{ height: '1.15rem', width: '1.15rem' }} color="light" /> Creating
                            </span>
                          ) : (
                            'Create'
                          )}
                        </Button>
                      )}
                    </div>
                  </form>
                )
              }}
            </Formik>
          </Col>
          <Col sm={0} md={1}></Col>
        </Row>
      </PageContainer>
    )
  }
}

const mapStateToProps = (state) => ({
  allCompanyList: state.emailReports.allCompanyList,
  companiesLoading: state.emailReports.companiesLoading,
  selectedCompany: state.emailReports.selectedCompany,
  dayOfMonthOptions: state.emailReports.dayOfMonthOptions,
  loadedReport: state.emailReports.loadedReport,
})

const mapDispatchToProps = {
  handleSubmitCreateEmailReportEntry,
  handleCompanySelectedEmailReport,
  handleSubmitSaveEmailReportEntry,
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateEmailReport)
